import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { generatePath, useHistory } from 'react-router-dom';
import { clearAlertMessage, clearCity, clearDeletedVehicles, clearFiles, clearVehicles } from 'app/store/slices/forms';
import { clearServiceDetails, clearServiceDocuments, fetchServiceDocuments } from 'app/store/slices/services';
import { clearNewRequest, clearRequestDetails, fetchNewRequest } from 'app/store/slices/requests';
import {
    fecthActualRevision,
    fecthAllRevisions,
    clearRevisions,
    clearActualRevision,
} from 'app/store/slices/revisions';
import { hideLoading, showLoading } from 'app/store/slices/settings';
import notification from 'app/config/notification';
import requestEndpoints from 'app/api/services/requestEndpoints';
import {
    DialogActions,
    Button,
    TextField,
    DialogContentText,
    DialogContent,
    DialogTitle,
    Dialog,
} from '@material-ui/core';
import { errorHandler } from 'app/utils/errorHandler';
import { clearInspectedVehicles, clearVehiclePlateByCompany } from 'app/store/slices/vehicle';
import { URLS } from 'app/config/urls';
import { SERVICES_CODES, SERVICES_GROUPS, SERVICES_PROPERTIES } from 'app/config/services';
import { STATUS_CODES } from 'app/config/status';

const withRequestInitiation = (BaseComponent) => (props) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const { intl } = props;

    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [openDialogJustification, setOpenDialogJustification] = useState(false);
    const [justification, setJustification] = useState('');

    const service = useSelector((state) => state.services.details);
    const request = useSelector((state) => state.requests.details);
    const documentsStore = useSelector((state) => state.services.documents);
    const activeCompany = useSelector((state) => state.company.activeCompany);
    const user = useSelector((state) => state.auth.user);

    const requestJsonData = useMemo(() => {
        return request.jsonSolicitacao ? JSON.parse(request.jsonSolicitacao) : {};
    }, [request]);

    if (Object.keys(service).length === 0) {
        history.push(generatePath(URLS.SERVICES));
    }

    const canEdit = useMemo(() => {
        if (service.cdServico === SERVICES_CODES.EXCLUSAO_VEICULOS) {
            return (
                !request.cdSituacao ||
                request.cdSituacao === STATUS_CODES.EM_CONSTRUCAO ||
                request.cdSituacao === STATUS_CODES.AGUARDANDO_RESOLUCAO_PENDENCIAS
            );
        }
        if (service.cdServico === SERVICES_CODES.INATIVACAO_CADASTRAL) return false;

        if (
            SERVICES_GROUPS.COMPANY.includes(service.cdServico) &&
            service.cdServico !== SERVICES_CODES.INSCRICAO_CADASTRAL &&
            service.cdServico !== SERVICES_CODES.CHANGE_EMAIL &&
            Object.keys(activeCompany).length === 0
        ) {
            return false;
        }

        if (SERVICES_GROUPS.SURVEY.includes(service.cdServico)) {
            if (user?.boVistoriador && request.cdSituacao) {
                if (request.cdSituacao !== STATUS_CODES.AGUARDANDO_VISTORIA) {
                    return false;
                } else if (request.cdSituacao === STATUS_CODES.AGUARDANDO_VISTORIA) {
                    //override request.situacao?.boEdicaoPortalExterno
                    return true;
                }
            }
        }

        if (!request.situacao) return true; // new request
        return request.situacao?.boEdicaoPortalExterno || request.cdSituacao === STATUS_CODES.EM_CONSTRUCAO;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [request, service]);

    const editing = useMemo(() => {
        return !!request.cdSolicitacao;
    }, [request]);

    useEffect(() => {
        (async () => {
            if (
                service.cdServico &&
                service.cdServico !== '' &&
                service.cdServico !== SERVICES_CODES.LICENCA_SEGUNDA_VIA
            ) {
                await dispatch(
                    fetchNewRequest(service.cdServico === SERVICES_CODES.CHANGE_EMAIL, {
                        cdServico: service.cdServico,
                    }),
                );
            }
        })();
    }, [dispatch, service.cdServico]);

    useEffect(() => {
        return () => {
            dispatch(clearAlertMessage());
            dispatch(clearCity());
            dispatch(clearFiles());
            dispatch(clearServiceDetails());
            dispatch(clearServiceDocuments());
            dispatch(clearVehicles());
            dispatch(clearNewRequest());
            dispatch(clearRequestDetails());
            dispatch(clearRevisions());
            dispatch(clearActualRevision());
            dispatch(clearVehiclePlateByCompany());
            dispatch(clearInspectedVehicles());
            dispatch(clearDeletedVehicles());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const init = async () => {
            const cdServico = service.cdServico ? service.cdServico : requestJsonData.cdServico;

            const search = {
                cdServico: cdServico,
            };

            let searchType = 1;
            let cdTipoEmpresa = null;

            if (cdServico && !SERVICES_GROUPS.LICENSE.includes(service.cdServico)) {
                if (requestJsonData.cdTipoPessoaFisica || activeCompany.cdTipoPessoaFisica) {
                    searchType = 2;
                    search.cdTipoPessoaFisica = parseInt(
                        requestJsonData.cdTipoPessoaFisica || activeCompany.cdTipoPessoaFisica,
                    );
                } else {
                    cdTipoEmpresa = requestJsonData.cdTipoEmpresa
                        ? requestJsonData.cdTipoEmpresa
                        : activeCompany.cdTipoEmpresa;
                    if (cdTipoEmpresa) {
                        search.cdTipoEmpresa = parseInt(cdTipoEmpresa);
                    }
                }
            }
            if (cdServico !== SERVICES_CODES.LICENCA_SEGUNDA_VIA && documentsStore.length === 0 && cdServico) {
                setLoadingDocuments(true);
                if (SERVICES_GROUPS.COMPANY.includes(service.cdServico) && searchType === 1 && !cdTipoEmpresa) {
                    dispatch(clearServiceDocuments());
                    setLoadingDocuments(false);
                    return;
                }
                await dispatch(fetchServiceDocuments(search));
                setLoadingDocuments(false);
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, requestJsonData, service]);

    useEffect(() => {
        const init = async () => {
            if (request.cdSituacao !== STATUS_CODES.EM_CONSTRUCAO && request.cdSolicitacao) {
                const promisses = [dispatch(fecthActualRevision(request.cdSolicitacao))];
                promisses.push(dispatch(fecthAllRevisions(request.cdSolicitacao)));
                await Promise.all(promisses);
            }
        };
        init();
    }, [dispatch, request]);

    const resetAndGo = useCallback(() => {
        dispatch(hideLoading());
        history.push(URLS.START);
    }, [dispatch, history]);

    const onCancel = useCallback(() => {
        if (request?.cdSolicitacao) {
            handleOpenDialogJustification();
        } else {
            if (window.confirm('Deseja cancelar? As informações não salvas serão perdidas.')) {
                history.push(generatePath(URLS.SERVICES));
            }
        }
    }, [history, request]);

    const validateForm = (functionValidation) => {
        functionValidation.then((res) => {
            if (Object.keys(res).length > 0) {
                notification('O(s) campo(s) sinalizado(s) precisam de revisão.', {
                    toastId: 'VALIDATION',
                    type: 'error',
                });
            }
        });
    };

    const handleCloseDialogJustification = () => {
        setJustification('');
        setOpenDialogJustification(false);
    };

    const handleOpenDialogJustification = () => {
        setJustification('');
        setOpenDialogJustification(true);
    };

    const renderDialogJustification = () => {
        return (
            <Dialog
                open={openDialogJustification}
                onClose={handleCloseDialogJustification}
                aria-labelledby="form-dialog-title">
                <DialogTitle id="form-dialog-title">Cancelamento</DialogTitle>
                <DialogContent>
                    <DialogContentText>Favor preencher a justificativa de cancelamento abaixo.</DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label=""
                        fullWidth
                        value={justification}
                        onChange={(e) => setJustification(e.target.value)}
                        inputProps={{
                            maxLength: 490,
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialogJustification} color="primary">
                        Fechar
                    </Button>
                    <Button onClick={() => handleCancelRequest()} color="primary">
                        Enviar
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const handleCancelRequest = async () => {
        if (justification === '') {
            notification(intl.formatMessage({ id: 'MESSAGES.REQUEST.REASON_MANDATORY' }), {
                toastId: 'REASON_MANDATORY',
                type: 'warning',
            });
            return;
        }
        dispatch(showLoading());
        const sendingData = new FormData();
        sendingData.append('cdSolicitacao', request.cdSolicitacao);
        sendingData.append('deJustificativa', justification);

        try {
            setOpenDialogJustification(false);
            const response = await requestEndpoints.cancelRequest(request.cdSolicitacao, sendingData);
            if (response?.status === 200) {
                notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.SUCCESSFUL_CANCEL_REQUEST' }), {
                    toastId: 'SUCCESSFUL_CANCEL_REQUEST',
                    type: 'success',
                });
                resetAndGo();
                return;
            }
        } catch (error) {
            notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.PROBLEM_CANCEL_REQUEST' }), {
                toastId: 'PROBLEM_CANCEL_REQUEST',
                type: 'error',
            });
            errorHandler(error.response?.status, error.response?.data?.message);
        }
        dispatch(hideLoading());
    };

    const handleOnBack = () => {
        if (request.nuSolicitacaoFmt && request.nuSolicitacaoFmt !== '') {
            history.push(URLS.START);
        } else {
            history.push(generatePath(URLS.SERVICES));
        }
    };

    const breadCrumb = [
        {
            title: intl.formatMessage({ id: 'LABELS.SERVICES.REQUEST' }),
            path: URLS.SERVICES,
        },
        {
            title: SERVICES_PROPERTIES[service.cdServico]?.title ? SERVICES_PROPERTIES[service.cdServico].title : '',
            path: '',
        },
    ];

    const rightInfo = request?.nuSolicitacaoFmt
        ? `${intl.formatMessage({ id: 'LABELS.SERVICES.REQUEST_NUMBER' })} ${request.nuSolicitacaoFmt}`
        : '';

    return (
        <>
            {renderDialogJustification()}
            <BaseComponent
                {...props}
                breadCrumb={breadCrumb}
                loadingDocuments={loadingDocuments}
                setLoadingDocuments={setLoadingDocuments}
                canEdit={canEdit}
                editing={editing}
                resetAndGo={resetAndGo}
                onCancel={onCancel}
                validateForm={validateForm}
                rightInfo={rightInfo}
                handleOnBack={handleOnBack}
            />
        </>
    );
};

export default withRequestInitiation;
